import type { Competition, Id, RuleSet, StandingRow, Team } from '../types.js';
import { clubKey, teamKey, teamNumber } from '../engine/keys.js';

/**
 * Mannschafts- und Spielerwertung (docs/13): Summen über die Paar-Tabelle.
 * Mannschaft = alle Paare mit demselben `club`; Spieler = beide Spieler eines Paars
 * bekommen dessen Ergebnisse (innerhalb eines Spieltags sind Paare fix — die
 * Spielerwertung wird über die Saison interessant, wenn Paare wechseln).
 */
export interface AggregateRow {
  key: string;
  name: string;
  /** beteiligte Team-IDs */
  teamIds: Id[];
  rank: number;
  played: number;
  wins: number;
  draws: number;
  losses: number;
  points: number;
  scoreFor: number;
  scoreAgainst: number;
  scoreDiff: number;
  /** Ø Punkte je Spiel — für Vergleich bei unterschiedlicher Spielanzahl */
  pointsPerGame: number;
}

function empty(key: string, name: string): AggregateRow {
  return { key, name, teamIds: [], rank: 0, played: 0, wins: 0, draws: 0, losses: 0, points: 0, scoreFor: 0, scoreAgainst: 0, scoreDiff: 0, pointsPerGame: 0 };
}

function add(agg: AggregateRow, r: StandingRow): void {
  agg.teamIds.push(r.teamId);
  agg.played += r.played;
  agg.wins += r.wins;
  agg.draws += r.draws;
  agg.losses += r.losses;
  agg.points += r.points;
  agg.scoreFor += r.scoreFor;
  agg.scoreAgainst += r.scoreAgainst;
  agg.scoreDiff = agg.scoreFor - agg.scoreAgainst;
  agg.pointsPerGame = agg.played > 0 ? agg.points / agg.played : 0;
}

/** Sortierung: Punkte → Siege−Niederlagen → Punktdifferenz → erzielte Punkte → Name */
export function rankAggregates(rows: AggregateRow[], _rules: RuleSet): AggregateRow[] {
  const sorted = rows
    .slice()
    .sort((x, y) => y.points - x.points || y.wins - y.losses - (x.wins - x.losses) || y.scoreDiff - x.scoreDiff || y.scoreFor - x.scoreFor || x.name.localeCompare(y.name, 'de'));
  let rank = 0;
  sorted.forEach((r, i) => {
    const prev = sorted[i - 1];
    const tie = prev && prev.points === r.points && prev.wins - prev.losses === r.wins - r.losses && prev.scoreDiff === r.scoreDiff && prev.scoreFor === r.scoreFor;
    if (!tie) rank = i + 1;
    r.rank = rank;
  });
  return sorted;
}

export function clubStandings(comp: Competition, rows: readonly StandingRow[]): AggregateRow[] {
  const byClub = new Map<string, AggregateRow>();
  for (const r of rows) {
    const team = comp.teams.find((t) => t.id === r.teamId);
    const club = team?.club?.trim() || 'Ohne Mannschaft';
    const key = club.toLowerCase().replace(/\s+/g, ' ');
    const agg = byClub.get(key) ?? empty(key, club);
    add(agg, r);
    byClub.set(key, agg);
  }
  return rankAggregates([...byClub.values()], comp.ruleSet);
}

/** Schlüssel über Spieltage hinweg: Groß/Klein, Leerzeichen und Punkte egal */
export function playerKey(firstName: string, lastName: string, club?: string): string {
  const norm = (x: string) =>
    x
      .toLowerCase()
      .replace(/[.\s]+/g, ' ')
      .trim();
  return `${norm(lastName)}, ${norm(firstName)}${club ? ` (${norm(club)})` : ''}`;
}

export function playerStandings(comp: Competition, rows: readonly StandingRow[]): AggregateRow[] {
  const byPlayer = new Map<string, AggregateRow>();
  for (const r of rows) {
    const team = comp.teams.find((t) => t.id === r.teamId);
    if (!team) continue;
    for (const p of team.players) {
      if (p.substitute) continue; // Ersatz zählt nicht für die Spielerwertung
      const key = playerKey(p.firstName, p.lastName, team.club);
      const name = `${p.firstName} ${p.lastName}`.trim() + (team.club ? ` (${team.club})` : '');
      const agg = byPlayer.get(key) ?? empty(key, name);
      add(agg, r);
      byPlayer.set(key, agg);
    }
  }
  return rankAggregates([...byPlayer.values()], comp.ruleSet);
}

/** Anzahl Spiele im Liga-Modus: Σ über Mannschaftspaare n_i · n_j */
export function interClubMatchCount(clubSizes: readonly number[]): number {
  let total = 0;
  for (let i = 0; i < clubSizes.length; i++) for (let j = i + 1; j < clubSizes.length; j++) total += (clubSizes[i] as number) * (clubSizes[j] as number);
  return total;
}

/**
 * Leistungsgruppen (docs/13 §7): je Nummer eine Gruppe, darin jedes Team gegen
 * jedes Team einer **anderen** Mannschaft. Für die Vorschau im Assistenten und
 * auf der Auslosungs-Seite — dieselbe Rechnung wie die Auslosung selbst.
 */
export function tierMatchCount(teams: readonly Pick<Team, 'name' | 'club'>[]): { groups: number; matches: number; sizes: number[] } {
  const nachNummer = new Map<number, Pick<Team, 'name' | 'club'>[]>();
  for (const team of teams) {
    const nr = teamNumber(team.name) ?? 0;
    nachNummer.set(nr, [...(nachNummer.get(nr) ?? []), team]);
  }
  let matches = 0;
  const sizes: number[] = [];
  for (const gruppe of nachNummer.values()) {
    sizes.push(gruppe.length);
    for (let i = 0; i < gruppe.length; i++) {
      for (let j = i + 1; j < gruppe.length; j++) {
        const ca = clubKey((gruppe[i] as Pick<Team, 'club'>).club);
        if (!ca || ca !== clubKey((gruppe[j] as Pick<Team, 'club'>).club)) matches++;
      }
    }
  }
  return { groups: nachNummer.size, matches, sizes };
}

/** Paar-Tabelle als Aggregat-Zeilen (Schlüssel = Name + Mannschaft) — für die Saison */
export function teamAggregates(comp: Competition, rows: readonly StandingRow[]): AggregateRow[] {
  const out: AggregateRow[] = [];
  for (const r of rows) {
    const team = comp.teams.find((t) => t.id === r.teamId);
    if (!team) continue;
    const agg = empty(teamKey(team), team.name);
    add(agg, r);
    out.push(agg);
  }
  return rankAggregates(out, comp.ruleSet);
}
